/**
 * Client intake Medplum Bot wrappers (email verification, registration, matching).
 * Provider/Supervisor roles do not receive FHIR Patient create — bots run with scoped policies.
 */

import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Identifier } from '@medplum/fhirtypes';

// Env var names kept as INTAKE_REGISTRATION_* for deployment compatibility; the bot was
// renamed to `client-intake-ticketing-master`, so the default identifier value points there.
export const INTAKE_REGISTRATION_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'client-intake-ticketing-master',
} as const;

const INTAKE_ACTION_NOT_REIMPLEMENTED =
  'This intake action is not yet reimplemented during the intake overhaul.';

function getIntakeRegistrationBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_INTAKE_REGISTRATION_BOT_ID?.trim();
  if (id) {
    return id;
  }
  const system =
    import.meta.env.VITE_MEDPLUM_INTAKE_REGISTRATION_BOT_IDENTIFIER_SYSTEM?.trim() ??
    INTAKE_REGISTRATION_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_INTAKE_REGISTRATION_BOT_IDENTIFIER_VALUE?.trim() ??
    INTAKE_REGISTRATION_BOT_IDENTIFIER.value;
  return { system, value };
}

export interface MintedPatientLogin {
  accessToken: string;
  refreshToken: string;
  profile: string;
  project: string;
}

export type IntakeSessionTypeForAvailability = 'intake' | 'consultation';

export interface ProviderAvailabilitySlotDto {
  start: string;
  end: string;
}

export interface GetProviderBookableDatesResult {
  providerId: string;
  sessionType: IntakeSessionTypeForAvailability;
  timeZone: string;
  availableWeekdays: string[];
  bookableDates: string[];
}

/** Dates in the booking window with at least one open slot (single server-side scan). */
export async function getProviderBookableDates(
  medplum: MedplumClient,
  input: {
    providerId: string;
    sessionType: IntakeSessionTypeForAvailability;
    timeZone?: string;
    durationMinutes?: number;
    slotStepMinutes?: number;
    minBookingHours?: number;
    maxBookingDays?: number;
    excludeAppointmentId?: string;
  },
): Promise<GetProviderBookableDatesResult> {
  return medplum.executeBot(
    getIntakeMatchingBotTarget(),
    {
      action: 'get-provider-bookable-dates',
      providerId: input.providerId,
      sessionType: input.sessionType,
      timeZone: input.timeZone,
      durationMinutes: input.durationMinutes,
      slotStepMinutes: input.slotStepMinutes,
      minBookingHours: input.minBookingHours,
      maxBookingDays: input.maxBookingDays,
      excludeAppointmentId: input.excludeAppointmentId,
    },
    ContentType.JSON,
  ) as Promise<GetProviderBookableDatesResult>;
}

/** Staff manual add-client: hold a proposed appointment without referral Task capacity. */
export async function holdIntakeProviderSlotViaBot(
  medplum: MedplumClient,
  input: {
    episodeOfCareId: string;
    sessionId: string;
    practitionerId: string;
    assignmentLabel?: string;
  },
): Promise<{ appointmentId: string }> {
  return medplum.executeBot(
    getIntakeRegistrationBotTarget(),
    {
      action: 'hold-intake-provider-slot',
      episodeOfCareId: input.episodeOfCareId,
      sessionId: input.sessionId,
      practitionerId: input.practitionerId,
      assignmentLabel: input.assignmentLabel,
    },
    ContentType.JSON,
  ) as Promise<{ appointmentId: string }>;
}

export async function ensurePracticeClientViaBot(
  medplum: MedplumClient,
  input: { patientId: string; organizationId: string },
): Promise<void> {
  await medplum.executeBot(
    getIntakeRegistrationBotTarget(),
    {
      action: 'ensure-practice-client',
      patientId: input.patientId,
      organizationId: input.organizationId,
    },
    ContentType.JSON,
  );
}

export interface ProvisionEpisodePanelResult {
  careTeamId: string;
  careTeamName?: string;
}

/** Staff-only: shared panel CareTeam + Patient/$set-accounts (bot admin — not browser FHIR). */
export async function provisionEpisodePanelViaBot(
  medplum: MedplumClient,
  input: { patientId: string; practitionerId: string; organizationId?: string },
): Promise<ProvisionEpisodePanelResult> {
  return medplum.executeBot(
    getIntakeRegistrationBotTarget(),
    {
      action: 'provision-episode-panel',
      patientId: input.patientId,
      practitionerId: input.practitionerId,
      organizationId: input.organizationId,
    },
    ContentType.JSON,
  ) as Promise<ProvisionEpisodePanelResult>;
}

export interface InviteIntakePatientResult {
  invited: boolean;
  patientId: string;
  reason?: 'already-exists';
}

// Env var names kept as INTAKE_MATCHING_* for deployment compatibility; the bot was
// renamed to `client-intake-matching`, so the default identifier value points there.
export const INTAKE_MATCHING_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'client-intake-matching',
} as const;

function getIntakeMatchingBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_INTAKE_MATCHING_BOT_ID?.trim();
  if (id) {
    return id;
  }
  const system =
    import.meta.env.VITE_MEDPLUM_INTAKE_MATCHING_BOT_IDENTIFIER_SYSTEM?.trim() ??
    INTAKE_MATCHING_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_INTAKE_MATCHING_BOT_IDENTIFIER_VALUE?.trim() ??
    INTAKE_MATCHING_BOT_IDENTIFIER.value;
  return { system, value };
}

export interface MatchedProvider {
  id: string;
  name: string;
  credentials: string;
  headshotUrl: string | null;
  gender: 'male' | 'female' | 'other';
  specialties: string[];
  format: ('in-person' | 'virtual')[];
  location: string;
  zip: string;
  acceptsInsurance: boolean;
  shortBio: string;
  longBio: string;
  languages: string[];
  referralTaskId: string;
}

/** Create portal login + branded setup email (via UserSecurityRequest subscription). */
export async function inviteIntakePatientViaBot(
  _medplum: MedplumClient,
  _input: {
    patientId: string;
    email: string;
    firstName: string;
    lastName: string;
    projectId?: string;
  },
): Promise<InviteIntakePatientResult> {
  throw new Error(INTAKE_ACTION_NOT_REIMPLEMENTED);
}
